import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Phone,
  Calendar,
  MapPin,
  AlertCircle,
  Stethoscope,
  ClipboardList,
  HeartPulse,
  CheckCircle2,
  ShieldCheck,
  ListChecks,
  ChevronRight,
} from 'lucide-react';
import { SYMPTOM_BY_SLUG, SYMPTOM_PAGES } from '../data/symptomPages';
import { clinicInfo } from '../data/mock';
import { API_BASE } from '../lib/apiBase';
import { useSeo } from '../hooks/useSeo';

const CLINIC_LAT = 26.13932093452639;
const CLINIC_LNG = 91.7890232810652;
const MAPS_LINK = `https://www.google.com/maps/dir/?api=1&destination=${CLINIC_LAT},${CLINIC_LNG}`;
const WHATSAPP_LINK = `https://wa.me/${(clinicInfo.whatsapp || '').replace(/[^\d]/g, '')}`;
const TEL_LINK = `tel:${clinicInfo.phone}`;

export const SymptomPage = () => {
  const params = useParams();
  const location = useLocation();
  // Routes are registered statically per slug, so useParams won't carry the slug.
  // Fall back to deriving from pathname (strip leading `/`).
  const slug = params.slug || location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const page = SYMPTOM_BY_SLUG[slug];
  const [doctor, setDoctor] = useState(null);

  useEffect(() => {
    if (!page) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/doctors/${page.doctorSlug}`);
        if (r.ok && !cancelled) setDoctor(await r.json());
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const jsonLd = useMemo(() => {
    if (!page) return [];
    const pageUrl = `https://healthwrx.in/${page.slug}`;
    const medicalWebPage = {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: page.metaTitle,
      description: page.metaDescription,
      url: pageUrl,
      inLanguage: 'en-IN',
      about: { '@type': 'MedicalCondition', name: page.aboutCondition },
      audience: 'https://schema.org/Patient',
      lastReviewed: '2026-05-27',
      dateModified: '2026-05-27',
      reviewedBy: {
        '@type': 'Physician',
        name: doctor?.name || 'Healthwrx specialist',
        medicalSpecialty: page.department,
        url: `https://healthwrx.in/doctors/${page.doctorSlug}`,
      },
      publisher: {
        '@type': 'MedicalClinic',
        name: 'Healthwrx',
        url: 'https://healthwrx.in',
      },
    };
    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [...page.faqs, ...page.aiBlock].map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const physician = {
      '@context': 'https://schema.org',
      '@type': 'Physician',
      name: doctor?.name || page.doctorSlug,
      medicalSpecialty: page.department,
      worksFor: {
        '@type': 'MedicalClinic',
        name: 'Healthwrx',
        address: {
          '@type': 'PostalAddress',
          streetAddress: `${clinicInfo.address.line1}, ${clinicInfo.address.line2}`,
          addressLocality: clinicInfo.address.city,
          postalCode: clinicInfo.address.pincode,
          addressRegion: clinicInfo.address.state,
          addressCountry: 'IN',
        },
      },
    };
    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      name: 'Healthwrx',
      url: 'https://healthwrx.in',
      telephone: clinicInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${clinicInfo.address.line1}, ${clinicInfo.address.line2}`,
        addressLocality: clinicInfo.address.city,
        postalCode: clinicInfo.address.pincode,
        addressRegion: clinicInfo.address.state,
        addressCountry: 'IN',
      },
      geo: { '@type': 'GeoCoordinates', latitude: CLINIC_LAT, longitude: CLINIC_LNG },
      openingHours: ['Mo-Sa 09:00-19:00'],
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://healthwrx.in/' },
        { '@type': 'ListItem', position: 2, name: 'Symptoms', item: 'https://healthwrx.in/symptoms' },
        {
          '@type': 'ListItem',
          position: 3,
          name: page.department,
          item: `https://healthwrx.in/symptoms?specialty=${encodeURIComponent(page.department)}`,
        },
        { '@type': 'ListItem', position: 4, name: page.aboutCondition, item: pageUrl },
      ],
    };
    return [medicalWebPage, faqPage, physician, localBusiness, breadcrumb];
  }, [page, doctor]);

  useSeo({
    title: page?.metaTitle,
    description: page?.metaDescription,
    canonical: page ? `https://healthwrx.in/${page.slug}` : undefined,
    jsonLd,
  });

  if (!page) return <Navigate to="/symptoms" replace />;

  const bookHref = `/book-appointment?service=${encodeURIComponent(page.department)}${doctor?.name ? `&doctor=${encodeURIComponent(doctor.name)}` : ''}`;

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid={`symptom-page-${page.slug}`}>
      {/* HERO */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 max-w-5xl text-white">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            data-testid="symptom-breadcrumb"
            className="flex items-center flex-wrap gap-x-1 gap-y-1 text-xs sm:text-sm text-white/85"
          >
            <Link to="/" className="hover:text-white transition" data-testid="crumb-home">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/50" aria-hidden="true" />
            <Link to="/symptoms" className="hover:text-white transition" data-testid="crumb-symptoms">Symptoms</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/50" aria-hidden="true" />
            <Link
              to={`/symptoms?specialty=${encodeURIComponent(page.department)}`}
              className="hover:text-white transition"
              data-testid="crumb-specialty"
            >
              {page.department}
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/50" aria-hidden="true" />
            <span className="text-white font-medium truncate max-w-[60vw] sm:max-w-none" aria-current="page" data-testid="crumb-current">
              {page.aboutCondition}
            </span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mt-4 mb-4">{page.h1}</h1>
          <p className="text-base md:text-lg text-gray-100 mb-6 max-w-3xl">{page.subheadline}</p>
          <div className="flex flex-wrap gap-3">
            <a href={TEL_LINK}>
              <Button size="lg" className="bg-white text-[#1C5C62] hover:bg-gray-100 font-semibold" data-testid="hero-call-btn">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </Button>
            </a>
            <Link to={bookHref}>
              <Button size="lg" className="bg-[#14D1A2] text-white hover:bg-[#10B88A]" data-testid="hero-book-btn">
                <Calendar className="w-4 h-4 mr-2" /> Book Appointment
              </Button>
            </Link>
            <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <MapPin className="w-4 h-4 mr-2" /> Directions
              </Button>
            </a>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-6xl py-12 lg:grid lg:grid-cols-[1fr_240px] lg:gap-10">
        <div className="space-y-10 lg:min-w-0" data-testid="symptom-main">
        {/* MEDICALLY REVIEWED BY + LAST REVIEWED */}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 border-l-4 border-[#14D1A2] bg-white px-4 py-3 rounded-r shadow-sm"
          data-testid="medically-reviewed-by"
          itemScope
          itemType="https://schema.org/MedicalWebPage"
        >
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#14D1A2]" aria-hidden="true" />
            <span>
              Medically reviewed by{' '}
              <Link
                to={`/doctors/${page.doctorSlug}`}
                className="font-semibold text-[#1C5C62] hover:underline"
                data-testid="reviewed-by-doctor"
              >
                {doctor ? doctor.name : 'Healthwrx specialist'}
              </Link>
              {doctor?.specialization ? `, ${doctor.specialization}` : ''}
            </span>
          </span>
          <span className="hidden sm:inline text-gray-300" aria-hidden="true">·</span>
          <span data-testid="last-reviewed-date">
            Last reviewed:{' '}
            <time dateTime="2026-05-27" itemProp="dateModified">27 May 2026</time>
          </span>
        </div>

        {/* GLANCE */}
        <Card id="glance" className="border-0 shadow-md scroll-mt-24">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">{page.aboutCondition} at a Glance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Most common causes</h3>
                <ul className="space-y-2">
                  {page.glance.causes.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#14D1A2] flex-shrink-0 mt-1" /> {c}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">When to seek medical attention</h3>
                <ul className="space-y-2">
                  {page.glance.seekAttention.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-gray-700"><AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" /> {c}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-6 text-sm text-gray-600"><strong>Specialist:</strong> {doctor?.name || page.doctorSlug} &middot; {page.department}</p>
          </CardContent>
        </Card>

        {/* WHAT IS */}
        <section id="what-is" className="scroll-mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">What is {page.aboutCondition}?</h2>
          <p className="text-gray-700 leading-relaxed">{page.whatIs}</p>
        </section>

        {/* CAUSES */}
        <section id="causes" className="scroll-mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">Common Causes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {page.causes.map((c) => (
              <Card key={c.title} className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{c.title}</h3>
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    {c.items.map((i) => (<li key={i}>• {i}</li>))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-6 p-5 bg-[#14D1A2]/10 border border-[#14D1A2]/30 rounded-lg flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-800 font-medium">{page.midCta}</p>
            <div className="flex gap-2 flex-shrink-0">
              <a href={TEL_LINK}><Button variant="outline">📞 Call</Button></a>
              <Link to={bookHref}><Button className="bg-[#14D1A2] hover:bg-[#10B88A] text-white">Book Now</Button></Link>
            </div>
          </div>
        </section>

        {/* RED FLAGS — softened heading + colors for confidential/mental-health pages */}
        <Card
          id="red-flags"
          className={`border-0 shadow-md border-l-4 scroll-mt-24 ${
            page.confidential ? 'border-l-amber-400' : 'border-l-red-500'
          }`}
        >
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className={`w-6 h-6 ${page.confidential ? 'text-amber-600' : 'text-red-600'}`} />{' '}
              {page.confidential ? "Signs It's Time to Reach Out" : 'Symptoms That Should Not Be Ignored'}
            </h2>
            <ul className="space-y-2 text-gray-700">
              {page.redFlags.map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <span className={`mt-1 ${page.confidential ? 'text-amber-500' : 'text-red-500'}`}>▸</span> {r}
                </li>
              ))}
            </ul>
            {page.extraNote && (
              <p className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 italic">{page.extraNote}</p>
            )}
          </CardContent>
        </Card>

        {/* EVALUATION */}
        <section id="evaluation" className="scroll-mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2"><ClipboardList className="w-6 h-6 text-[#14D1A2]" /> How Doctors Evaluate {page.aboutCondition}</h2>
          <p className="text-gray-700 leading-relaxed">{page.evaluation}</p>
        </section>

        {/* CARE */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><HeartPulse className="w-6 h-6 text-[#14D1A2]" /> Available Care at Healthwrx</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {page.careOptions.map((c) => (
                <div key={c} className="flex items-start gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#14D1A2] flex-shrink-0 mt-1" /> {c}</div>
              ))}
            </div>
            <Link to={bookHref}>
              <Button size="lg" className="mt-6 bg-[#14D1A2] hover:bg-[#10B88A] text-white" data-testid="care-book-btn">
                📅 Book a consultation
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* DOCTOR */}
        {doctor && (
          <Card id="specialist" className="border-0 shadow-md scroll-mt-24">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2"><Stethoscope className="w-6 h-6 text-[#14D1A2]" /> Meet the Specialist</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="aspect-square max-w-[240px] mx-auto md:mx-0 rounded-2xl overflow-hidden bg-gray-100">
                  <img
                    src={doctor.image}
                    alt={`${doctor.name}, ${doctor.specialization} specialist at Healthwrx Guwahati`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=14D1A2&color=fff&size=400`; }}
                  />
                </div>
                <div className="md:col-span-2">
                  <h3 className="text-xl font-bold text-gray-900">{doctor.name}</h3>
                  <p className="text-[#14D1A2] font-semibold">{doctor.specialization}</p>
                  <p className="text-sm text-gray-600 mt-2">{doctor.education} &middot; {doctor.experience}</p>
                  <p className="text-gray-700 mt-3">{doctor.bio}</p>
                  <div className="flex gap-2 mt-4">
                    <Link to={`/doctors/${doctor.slug || doctor.id}`}>
                      <Button variant="outline" data-testid="doctor-profile-link">View full profile</Button>
                    </Link>
                    <Link to={bookHref}>
                      <Button className="bg-[#14D1A2] hover:bg-[#10B88A] text-white">Book Consultation</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQs */}
        <section id="faqs" className="scroll-mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {page.faqs.map((f, i) => (
              <AccordionItem key={`${i}-${f.q}`} value={`faq-${i}`} className="bg-white border-0 shadow-sm rounded-lg px-6">
                <AccordionTrigger className="text-base md:text-lg font-semibold text-gray-900 hover:text-[#14D1A2] py-4 text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* LOCAL */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{page.aboutCondition} Care in Guwahati</h2>
            <p className="text-gray-700 leading-relaxed">
              Patients from {page.localities.slice(0, -1).join(', ')} and {page.localities.slice(-1)[0]} can consult our {page.department} specialist at Healthwrx. {page.localBlurb}
            </p>
          </CardContent>
        </Card>

        {/* RELATED */}
        {page.related && page.related.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Pages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {page.related.map((rslug) => {
                const r = SYMPTOM_BY_SLUG[rslug];
                if (!r) return null;
                return (
                  <Link
                    key={rslug}
                    to={`/${rslug}`}
                    className="block p-4 bg-white border-0 shadow-sm rounded-lg hover:shadow-md transition-shadow"
                    data-testid={`related-${rslug}`}
                  >
                    <p className="text-sm text-gray-500">{r.department}</p>
                    <p className="font-semibold text-gray-900 mt-1">{r.aboutCondition}</p>
                  </Link>
                );
              })}
              <Link to="/symptoms" className="block p-4 bg-white border-0 shadow-sm rounded-lg hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-500">All symptom guides</p>
                <p className="font-semibold text-gray-900 mt-1">Browse all symptoms &rarr;</p>
              </Link>
            </div>
          </section>
        )}

        {/* BOTTOM CTA */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
          <CardContent className="p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Concerned about {page.aboutCondition.toLowerCase()}?</h2>
            <p className="text-gray-100 mb-6">Book an appointment with our {page.department.toLowerCase()} specialist today.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href={TEL_LINK}><Button size="lg" className="bg-white text-[#1C5C62] hover:bg-gray-100"><Phone className="w-4 h-4 mr-2" /> Call</Button></a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"><Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">WhatsApp</Button></a>
              <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10"><MapPin className="w-4 h-4 mr-2" /> Directions</Button></a>
              <Link to={bookHref}><Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white border border-white/30"><Calendar className="w-4 h-4 mr-2" /> Book Online</Button></Link>
            </div>
          </CardContent>
        </Card>

        {/* AI BLOCK (quick Q&A) */}
        <Card className="border-0 shadow-sm bg-slate-50">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#14D1A2]" /> Common Questions</h2>
            <div className="space-y-4">
              {page.aiBlock.map((q) => (
                <div key={q.q}>
                  <p className="font-semibold text-gray-900">{q.q}</p>
                  <p className="text-gray-700 mt-1">{q.a}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>

        {/* IN-THIS-GUIDE TOC (desktop only) */}
        <GuideToc hasDoctor={Boolean(doctor)} confidential={Boolean(page.confidential)} />
      </div>
    </div>
  );
};

const TOC_ITEMS = [
  { id: 'glance', label: 'At a Glance' },
  { id: 'what-is', label: 'What is' },
  { id: 'causes', label: 'Common Causes' },
  { id: 'red-flags', label: 'Red Flags' },
  { id: 'evaluation', label: 'Evaluation' },
  { id: 'specialist', label: 'Meet the Specialist' },
  { id: 'faqs', label: 'FAQs' },
];

const GuideToc = ({ hasDoctor, confidential }) => {
  const items = useMemo(
    () =>
      TOC_ITEMS.map((it) =>
        it.id === 'red-flags' && confidential ? { ...it, label: 'When to Reach Out' } : it
      ),
    [confidential]
  );
  const [active, setActive] = useState(items[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      // Trigger when section enters the upper 40% of the viewport
      { rootMargin: '-100px 0px -55% 0px', threshold: 0 }
    );
    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // Re-attach when the async specialist card mounts so its section is tracked too.
  }, [hasDoctor]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Update hash without jump
    window.history.replaceState(null, '', `#${id}`);
    setActive(id);
  };

  return (
    <aside className="hidden lg:block" data-testid="guide-toc">
      <nav className="sticky top-24 border-l border-gray-200 pl-5">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-2">
          <ListChecks className="w-3.5 h-3.5 text-[#14D1A2]" /> In this guide
        </p>
        <ul className="space-y-2.5 text-sm">
          {items.map(({ id, label }) => {
            const isActive = active === id;
            return (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => handleClick(e, id)}
                  data-testid={`toc-link-${id}`}
                  className={`block leading-snug transition-colors relative ${
                    isActive
                      ? 'text-[#1C5C62] font-semibold'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute -left-[21px] top-1 bottom-1 w-[2px] bg-[#14D1A2] rounded"
                    />
                  )}
                  {label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

// ===== Symptoms index page moved to ./SymptomsIndex.jsx =====
